import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Box,
  Button,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { styled } from "@mui/material/styles";
import { FroalaRichText } from "../../util/FroalaRichtext";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { getKeyProvisionGlobalNew } from "../../../redux/slices/keyProvisionGlobalNewSlice";
import { updatekeyProvisionGlobalData } from "../../../redux/slices/updateDataSlice";
import { CommentKey, CommentSectionName } from "../../../constant";
import { saveDapCommentData } from "../../../redux/slices/saveCommentSlice";
import {
  updateDapCommentData,
  DapComment,
  getDapComments,
} from "../../../redux/slices/updateDapCommentSlice";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

interface JvPartner {
  firmName?: string;
  keyContacts?: string;
  email?: string;
  riskRating?: number;
}

interface KeyProvisionForEmacApacProps {
  mode?: string;
}

export const KeyProvisionForEmacApac = (
  props: KeyProvisionForEmacApacProps,
) => {
  const dispatch = useAppDispatch();
  const [jvPartnerData, setJvPartnerData] = useState<JvPartner[]>([]);
  const [showJvPartners, setShowJvPartners] = useState(false);

  const assetPlanId = useAppSelector(
    (state) => state.getAssetPlanId.assetPlanId,
  );
  const apiData = useAppSelector((state) => state.keyProvisionGlobalNew.data);
  const globalKeyProvisionsData = useAppSelector(
    (state) => state.updateData.keyProvisionGlobalData,
  ) || {
    planningAdviser: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    buildingContractors: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    leasingAgents: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    propertyManagingAgent: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    operator: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    thirdPartyAssetManager: {
      firmName: "",
      keyContacts: "",
      email: "",
      riskRating: undefined,
    },
    jvPartnerData: [],
    holdingStructure: { specialPurposeVehicle: "", holdingCompany: "" },
    thirdPartyInsurer: {
      firmName: "",
      keyContacts: "",
      email: "",
      phoneNumber: undefined,
    },
  };
  const loading = useAppSelector(
    (state) => state.keyProvisionGlobalNew.loading,
  );
  const error = useAppSelector((state) => state.keyProvisionGlobalNew.error);
  const keyProvisionsComment = useAppSelector(
    (state) => state.dapComments?.data,
  );

  useEffect(() => {
    if (assetPlanId) {
      console.log("Calling API with AssetPlanId:", assetPlanId);
      dispatch(getKeyProvisionGlobalNew(assetPlanId));
      dispatch(getDapComments(assetPlanId));
    }
  }, [assetPlanId, dispatch]);

  useEffect(() => {
    if (apiData) {
      dispatch(updatekeyProvisionGlobalData(apiData));
      if (apiData.jvPartnerData && apiData.jvPartnerData.length > 0) {
        setJvPartnerData(apiData.jvPartnerData);
        setShowJvPartners(true);
      }
    }
  }, [apiData, dispatch]);

  const handleGlobalDataChange = (
    section: string,
    field: string,
    value: any,
  ) => {
    const updatedData = {
      ...globalKeyProvisionsData,
      [section]: {
        ...globalKeyProvisionsData[section],
        [field]: value,
      },
    };
    dispatch(updatekeyProvisionGlobalData(updatedData));
  };

  const handleJvPartnerDataChange = (
    index: number,
    field: string,
    value: any,
  ) => {
    const newJvPartnerData = [...jvPartnerData];
    newJvPartnerData[index] = {
      ...newJvPartnerData[index],
      [field]: value,
    };
    setJvPartnerData(newJvPartnerData);
    dispatch(
      updatekeyProvisionGlobalData({
        ...globalKeyProvisionsData,
        jvPartnerData: newJvPartnerData,
      }),
    );
  };

  const addJvPartner = () => {
    setShowJvPartners(true);
    const newJvPartnerData = [
      ...jvPartnerData,
      { firmName: "", keyContacts: "", email: "", riskRating: undefined },
    ];
    setJvPartnerData(newJvPartnerData);
    dispatch(
      updatekeyProvisionGlobalData({
        ...globalKeyProvisionsData,
        jvPartnerData: newJvPartnerData,
      }),
    );
  };

  const removeJvPartner = (index: number) => {
    const filteredJvPartnerData = jvPartnerData.filter((_, i) => i !== index);
    if (filteredJvPartnerData.length === 0) {
      setShowJvPartners(false);
    }
    setJvPartnerData(filteredJvPartnerData);
    dispatch(
      updatekeyProvisionGlobalData({
        ...globalKeyProvisionsData,
        jvPartnerData: filteredJvPartnerData,
      }),
    );
  };

  const handleCommentChange = (model: any, commentKey: any) => {
    const getCurrentComment = keyProvisionsComment.find(
      (x) => x.commentKey.toLowerCase() === commentKey.toLowerCase(),
    );
    const updatedcomment: DapComment = {
      id: getCurrentComment ? getCurrentComment.id : 0,
      commentKey: getCurrentComment ? getCurrentComment.commentKey : commentKey,
      commentText: model || "",
      sectionName: CommentSectionName.KEYPROVISIONS,
      assetPlanId: assetPlanId,
    };

    dispatch(updateDapCommentData(updatedcomment));
    dispatch(saveDapCommentData(updatedcomment));
  };

  const mangementComment_date =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.MANAGEMENTAGREEMENT_DATE.toLowerCase(),
    )?.commentText || "";
  const mangementComment_fee =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.MANAGEMENTAGREEMENT_FEE.toLowerCase(),
    )?.commentText || "";
  const mangementComment_otherFee =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.MANAGEMENTAGREEMENT_OTHERFEE.toLowerCase(),
    )?.commentText || "";
  const leasebroker_name =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.LEASEBROKER_NAME.toLowerCase(),
    )?.commentText || "";
  const leasebroker_fee =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() === CommentKey.LEASEBROKER_FEE.toLowerCase(),
    )?.commentText || "";
  const leasebroker_otherfee =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.LEASEBROKER_OTHERFEE.toLowerCase(),
    )?.commentText || "";
  const jvagreement_KeyDecision =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.JVAGREEMENT_KEYDECISION.toLowerCase(),
    )?.commentText || "";
  const jvagreement_liquidProvision =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.JVAGREEMENT_LIQUIDITYPROVISION.toLowerCase(),
    )?.commentText || "";
  const jvagreement_fee =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() === CommentKey.JVAGREEMENT_FEE.toLowerCase(),
    )?.commentText || "";
  const jvagreement_Keyprovision =
    keyProvisionsComment?.find(
      (x) =>
        x.commentKey.toLowerCase() ===
        CommentKey.JVAGREEMENT_KEYPROVISION.toLowerCase(),
    )?.commentText || "";

  return (
    <div>
      <Box sx={{ flexGrow: 1 }} className="summary-form">
        <div className="tab-heading text-center">
          <h3 className="heading">Key Provisions</h3>
        </div>
        {loading ? (
          <Skeleton
            variant="rounded"
            width="100%"
            height={200}
            animation="wave"
          />
        ) : (
          <div>
            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                marginBottom: 3,
                boxShadow: 1,
                "& .MuiTable-root": {
                  borderCollapse: "collapse",
                  minWidth: "100%",
                },
                "& .MuiTableCell-root": {
                  border: "1px solid rgba(224, 224, 224, 1)",
                  padding: "4px 16px",
                  fontSize: "0.875rem",
                  height: "40px",
                },
                "& .MuiTableRow-root": {
                  height: "40px",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "5px",
                }}
              >
                <h3 style={{ marginTop: "0px" }}>Key Contacts</h3>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "400",
                    fontStyle: "italic",
                    color: "#666",
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    marginLeft: "8px",
                  }}
                >
                  EMEA/APAC
                </span>
              </div>
              <Table
                aria-label="key contacts table"
                size="small"
                sx={{ "& .MuiTableCell-sizeSmall": { padding: "2px 16px" } }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#253746" }}>
                    <TableCell sx={{ color: "white" }}></TableCell>
                    <TableCell sx={{ color: "white" }}>Firm Name</TableCell>
                    <TableCell sx={{ color: "white" }}>Key Contact</TableCell>
                    <TableCell sx={{ color: "white" }}>Email</TableCell>
                    <TableCell sx={{ color: "white" }}>Risk rating</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Planning Advisor
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.planningAdviser?.firmName ||
                            ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "planningAdviser",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.planningAdviser?.firmName
                      )}
                    </TableCell>

                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.planningAdviser
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "planningAdviser",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.planningAdviser?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.planningAdviser?.email || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "planningAdviser",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.planningAdviser?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.planningAdviser
                                ?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "planningAdviser",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.planningAdviser?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Building Contractors
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.buildingContractors
                              ?.firmName || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "buildingContractors",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.buildingContractors?.firmName
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.buildingContractors
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "buildingContractors",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.buildingContractors?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.buildingContractors
                              ?.email || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "buildingContractors",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.buildingContractors?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.buildingContractors
                                ?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "buildingContractors",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.buildingContractors?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Leasing agent(s)
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.leasingAgents?.firmName ||
                            ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "leasingAgents",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.leasingAgents?.firmName
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.leasingAgents
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "leasingAgents",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.leasingAgents?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.leasingAgents?.email || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "leasingAgents",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.leasingAgents?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.leasingAgents
                                ?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "leasingAgents",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.leasingAgents?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Property management agent
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.propertyManagingAgent
                              ?.firmName || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "propertyManagingAgent",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.propertyManagingAgent?.firmName
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.propertyManagingAgent
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "propertyManagingAgent",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.propertyManagingAgent
                          ?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.propertyManagingAgent
                              ?.email || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "propertyManagingAgent",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.propertyManagingAgent?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.propertyManagingAgent
                                ?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "propertyManagingAgent",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.propertyManagingAgent
                          ?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Operator
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.operator?.firmName || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "operator",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.operator?.firmName
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.operator?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "operator",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.operator?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={globalKeyProvisionsData.operator?.email || ""}
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "operator",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.operator?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.operator?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "operator",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.operator?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Third party Asset manager
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyAssetManager
                              ?.firmName || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyAssetManager",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.thirdPartyAssetManager?.firmName
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyAssetManager
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyAssetManager",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.thirdPartyAssetManager
                          ?.keyContacts
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyAssetManager
                              ?.email || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyAssetManager",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.thirdPartyAssetManager?.email
                      )}
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <FormControl fullWidth>
                          <Select
                            value={
                              globalKeyProvisionsData.thirdPartyAssetManager
                                ?.riskRating || ""
                            }
                            onChange={(e) =>
                              handleGlobalDataChange(
                                "thirdPartyAssetManager",
                                "riskRating",
                                e.target.value,
                              )
                            }
                            size="small"
                            sx={{
                              height: "32px",
                              "& .MuiSelect-select": {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                                padding: "4px 14px",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.23)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#1976d2",
                                },
                            }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        globalKeyProvisionsData.thirdPartyAssetManager
                          ?.riskRating
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>JV Partner(s)</span>
                        <Button
                          size="small"
                          onClick={addJvPartner}
                          sx={{
                            minWidth: "20px",
                            height: "20px",
                            padding: "0",
                            backgroundColor: "#4caf50",
                            color: "white",
                            borderRadius: "20%",
                            fontSize: "18px",
                            fontWeight: "bold",
                            "&:hover": {
                              backgroundColor: "#45a049",
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <AddIcon sx={{ fontSize: "16px" }} />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {showJvPartners &&
                    jvPartnerData.map((partner, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>JV Partner {index + 1}</span>
                            <Button
                              size="small"
                              onClick={() => removeJvPartner(index)}
                              sx={{
                                minWidth: "20px",
                                height: "20px",
                                padding: "0",
                                backgroundColor: "#f44336",
                                color: "white",
                                borderRadius: "20%",
                                fontSize: "18px",
                                fontWeight: "bold",
                                "&:hover": {
                                  backgroundColor: "#d32f2f",
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: "16px" }} />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            value={partner.firmName || ""}
                            onChange={(e) =>
                              handleJvPartnerDataChange(
                                index,
                                "firmName",
                                e.target.value,
                              )
                            }
                            InputProps={{
                              style: {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                              },
                            }}
                            sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            value={partner.keyContacts || ""}
                            onChange={(e) =>
                              handleJvPartnerDataChange(
                                index,
                                "keyContacts",
                                e.target.value,
                              )
                            }
                            InputProps={{
                              style: {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                              },
                            }}
                            sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            value={partner.email || ""}
                            onChange={(e) =>
                              handleJvPartnerDataChange(
                                index,
                                "email",
                                e.target.value,
                              )
                            }
                            InputProps={{
                              style: {
                                margin: "0px",
                                fontFamily:
                                  "Roboto, Helvetica, Arial, sans-serif",
                                fontSize: "0.875rem",
                                color: "rgba(0, 0, 0, 0.6)",
                              },
                            }}
                            sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth>
                            <Select
                              value={partner.riskRating || ""}
                              size="small"
                              onChange={(e) =>
                                handleJvPartnerDataChange(
                                  index,
                                  "riskRating",
                                  e.target.value,
                                )
                              }
                              sx={{
                                height: "32px",
                                "& .MuiSelect-select": {
                                  margin: "0px",
                                  fontFamily:
                                    "Roboto, Helvetica, Arial, sans-serif",
                                  fontSize: "0.875rem",
                                  color: "rgba(0, 0, 0, 0.6)",
                                  padding: "4px 14px",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "rgba(0, 0, 0, 0.23)",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "rgba(0, 0, 0, 0.87)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#1976d2",
                                  },
                              }}
                            >
                              <MenuItem value={1}>1</MenuItem>
                              <MenuItem value={2}>2</MenuItem>
                              <MenuItem value={3}>3</MenuItem>
                              <MenuItem value={4}>4</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                marginBottom: 3,
                boxShadow: 1,
                "& .MuiTable-root": {
                  borderCollapse: "collapse",
                  minWidth: "100%",
                },
                "& .MuiTableCell-root": {
                  border: "1px solid rgba(224, 224, 224, 1)",
                  padding: "4px 16px",
                  fontSize: "0.875rem",
                  height: "40px",
                },
                "& .MuiTableRow-root": {
                  height: "40px",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "5px",
                }}
              >
                <h3 style={{ marginTop: "0px" }}>Holding Structure</h3>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "400",
                    fontStyle: "italic",
                    color: "#666",
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    marginLeft: "8px",
                  }}
                >
                  EMEA/APAC
                </span>
              </div>
              <Table
                aria-label="holding structure table"
                size="small"
                sx={{ "& .MuiTableCell-sizeSmall": { padding: "2px 16px" } }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#253746" }}>
                    <TableCell sx={{ color: "white" }}>
                      Structure Type
                    </TableCell>
                    <TableCell sx={{ color: "white" }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Special purpose vehicle
                    </TableCell>
                    <TableCell>
                      <div
                        style={{
                          padding: "6px 14px",
                          border: "1px solid rgba(0, 0, 0, 0.23)",
                          borderRadius: "4px",
                          backgroundColor: "#f5f5f5",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontSize: "0.875rem",
                          color: "rgba(0, 0, 0, 0.6)",
                          height: "32px",
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        {globalKeyProvisionsData.holdingStructure
                          ?.specialPurposeVehicle || ""}
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Holding company
                    </TableCell>
                    <TableCell>
                      {props?.mode === "edit" ? (
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.holdingStructure
                              ?.holdingCompany || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "holdingStructure",
                              "holdingCompany",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      ) : (
                        globalKeyProvisionsData.holdingStructure?.holdingCompany
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                marginBottom: 3,
                boxShadow: 1,
                "& .MuiTable-root": {
                  borderCollapse: "collapse",
                  minWidth: "100%",
                },
                "& .MuiTableCell-root": {
                  border: "1px solid rgba(224, 224, 224, 1)",
                  padding: "4px 16px",
                  fontSize: "0.875rem",
                  height: "40px",
                },
                "& .MuiTableRow-root": {
                  height: "40px",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "5px",
                }}
              >
                <h3 style={{ marginTop: "0px" }}>Third-Party Insurer</h3>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "400",
                    fontStyle: "italic",
                    color: "#666",
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    marginLeft: "8px",
                  }}
                >
                  EMEA/APAC
                </span>
              </div>
              <div
                style={{
                  padding: "5px 16px",
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                }}
              >
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={
                      globalKeyProvisionsData.hasThirdPartyInsurer
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) => {
                      if (props?.mode === "edit") {
                        const hasInsurer = e.target.value === "yes";

                        const updatedData = {
                          ...globalKeyProvisionsData,
                          hasThirdPartyInsurer: hasInsurer,
                        };

                        if (!hasInsurer) {
                          updatedData.thirdPartyInsurer = {
                            firmName: "",
                            keyContacts: "",
                            email: "",
                            phoneNumber: undefined,
                          };
                        }

                        dispatch(updatekeyProvisionGlobalData(updatedData));
                      }
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={props?.mode !== "edit"} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={props?.mode !== "edit"} />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              {globalKeyProvisionsData.hasThirdPartyInsurer && (
                <Table
                  aria-label="third party insurer table"
                  size="small"
                  sx={{ "& .MuiTableCell-sizeSmall": { padding: "2px 16px" } }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#253746" }}>
                      <TableCell sx={{ color: "white" }}>Firm</TableCell>
                      <TableCell sx={{ color: "white" }}>Key Contact</TableCell>
                      <TableCell sx={{ color: "white" }}>Email</TableCell>
                      <TableCell sx={{ color: "white" }}>
                        Phone Number
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyInsurer
                              ?.firmName || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyInsurer",
                              "firmName",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyInsurer
                              ?.keyContacts || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyInsurer",
                              "keyContacts",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyInsurer?.email ||
                            ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyInsurer",
                              "email",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          margin="dense"
                          value={
                            globalKeyProvisionsData.thirdPartyInsurer
                              ?.phoneNumber || ""
                          }
                          onChange={(e) =>
                            handleGlobalDataChange(
                              "thirdPartyInsurer",
                              "phoneNumber",
                              e.target.value,
                            )
                          }
                          InputProps={{
                            style: {
                              margin: "0px",
                              fontFamily:
                                "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                            },
                          }}
                          sx={{ "& .MuiInputBase-root": { height: "32px" } }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <TableContainer
              component={Paper}
              sx={{
                maxWidth: "100%",
                marginBottom: 3,
                boxShadow: 1,
                "& .MuiTable-root": {
                  borderCollapse: "collapse",
                  minWidth: "100%",
                },
                "& .MuiTableCell-root": {
                  border: "1px solid rgba(224, 224, 224, 1)",
                  padding: "4px 16px",
                  fontSize: "0.875rem",
                  height: "40px",
                },
                "& .MuiTableRow-root": {
                  height: "40px",
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: "5px",
                }}
              >
                <h3 style={{ marginTop: "0px" }}>Agreement Provisions</h3>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "400",
                    fontStyle: "italic",
                    color: "#666",
                    backgroundColor: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    marginLeft: "8px",
                  }}
                >
                  EMEA/APAC
                </span>
              </div>

              <h3 className="text-left sub-heading">Management Agreement</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Name/ Parties/ Date</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={mangementComment_date}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.MANAGEMENTAGREEMENT_DATE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={mangementComment_date} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Management Fees</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={mangementComment_fee}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.MANAGEMENTAGREEMENT_FEE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={mangementComment_fee} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Other Fees</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={mangementComment_otherFee}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.MANAGEMENTAGREEMENT_OTHERFEE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={mangementComment_otherFee} />
                      )}
                    </div>
                  </Item>
                </Grid>
              </Grid>

              <h3 className="text-left sub-heading">Leasing Broker</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Name</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={leasebroker_name}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.LEASEBROKER_NAME,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={leasebroker_name} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Management Fees</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={leasebroker_fee}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.LEASEBROKER_FEE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={leasebroker_fee} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Item>
                    <h3 className="text-left">Other Fees</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={leasebroker_otherfee}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.LEASEBROKER_OTHERFEE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={leasebroker_otherfee} />
                      )}
                    </div>
                  </Item>
                </Grid>
              </Grid>

              <h3 className="text-left sub-heading">JV Agreement</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Item>
                    <h3 className="text-left">Key Major Decisions</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={jvagreement_KeyDecision}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.JVAGREEMENT_KEYDECISION,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={jvagreement_KeyDecision} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Item>
                    <h3 className="text-left">Fees</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={jvagreement_fee}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.JVAGREEMENT_FEE,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={jvagreement_fee} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Item>
                    <h3 className="text-left">Liquidity Provisions</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={jvagreement_liquidProvision}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.JVAGREEMENT_LIQUIDITYPROVISION,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={jvagreement_liquidProvision} />
                      )}
                    </div>
                  </Item>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Item>
                    <h3 className="text-left">Other Key Provisions</h3>
                    <div className="grid-item-box">
                      {props?.mode === "edit" ? (
                        <FroalaRichText
                          model={jvagreement_Keyprovision}
                          handlesModelChange={(model) =>
                            handleCommentChange(
                              model,
                              CommentKey.JVAGREEMENT_KEYPROVISION,
                            )
                          }
                        />
                      ) : (
                        <FroalaEditorView model={jvagreement_Keyprovision} />
                      )}
                    </div>
                  </Item>
                </Grid>
              </Grid>
            </TableContainer>
          </div>
        )}
      </Box>
    </div>
  );
};
